import { CollapsibleSection } from '@/components/candidate-job-slider-v1/components/overview/offer/components/CollapsibleSection';
import { OfferDetailsSection } from '@/components/candidate-job-slider-v1/components/overview/offer/components/OfferDetailsSection';
import { useShowToast } from '@/hooks';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { ViewFile } from '@/components/ats/FileUpload/ViewFile';
import {
  useApproveRejectActionMutation,
  useLazyGetApprovalsByEntityQuery,
} from '@/store/query/approval-builder';
import { useLazyGetOfferByIdQuery } from '@/store/query/offer-Initiation';
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
  useDisclosure,
  Center,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { LinkExternal02 } from '@kula-ai/icons/dual-tone/LinkExternal02';
import { MinusCircle } from '@kula-ai/icons/outline/MinusCircle';
import { useState, useEffect } from 'react';
import { ApprovalFlowSteps } from '../ApprovalFlowSteps';
import { Drawer } from '../drawer';
import { ModalWithForm } from '@/components/ats';
import { KText, KModal } from '@/ui-library/components';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { MOBILE_BREAKPOINT } from '@/constants/mobile-breakpoints';
import { useAuth } from '@/hooks/use-auth';
import { useHistory } from 'react-router-dom';
import { getOfferDocumentFiles } from '@/utils/offerUtils';
import { X } from '@kula-ai/icons-core';

export const OfferApprovalView = ({
  modalActions,
  offerId,
  onSuccess = () => {},
  hideApprovalStatus = false,
}) => {
  const isMobile = useIsMobile({ mobileBreakpoint: MOBILE_BREAKPOINT });
  const { isBootstraping } = useAuth();
  const history = useHistory();

  const rejectApproveFields = [
    {
      name: 'note',
      type: 'textArea',
      label: 'Notes',
      maxLength: 250,
      placeholder: 'Write your reason here',
    },
  ];

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const approverActions = useDisclosure();
  const modalSize = useBreakpointValue({ base: 'full', md: 'md' });

  const [getCurrentOffer, { data: currentOffer, isFetching: isOfferFetching }] =
    useLazyGetOfferByIdQuery({});

  const [
    getApprovalData,
    { data: currentApproval, isFetching: isCurrentApprovalLoading },
  ] = useLazyGetApprovalsByEntityQuery();

  const { currUserId } = useAtsPermissions();
  const statusMenuActions = useDisclosure();
  const {
    offer_status,
    candidate_id,
    candidate_name,
    requisition,
    ats_application_id,
    ats_job_id,
  } = currentOffer || {};

  // Prefer the offer's top-level ats_job_id / ats_application_id; fall back to the
  // requisition-group member for legacy responses missing the direct ids.
  const reqMember = requisition?.group?.members?.find(
    (m: any) => m?.id === requisition?.id
  );

  // Candidate link → the candidate's job page.
  const goToCandidateJob = () => {
    const jobId = ats_job_id || reqMember?.ats_job_id;
    if (!jobId) return;
    const appId = ats_application_id || reqMember?.ats_application_id;
    const query = appId
      ? `?appId=${appId}&currentView=stage&tab=candidates`
      : '';
    history.push(`/jobs/${jobId}${query}`);
    modalActions?.onClose();
  };

  // Docs for the approver: generated docs + attachments, or once e-signed the
  // consolidated signed offer + attachments (CORE-4774 / CORE-4794).
  const offerFiles = getOfferDocumentFiles(currentOffer);
  const isPendingApproval = offer_status === 'pending_approval';

  useEffect(() => {
    if (offerId) {
      getCurrentOffer({ id: offerId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  useEffect(() => {
    if (isPendingApproval) {
      getApprovalData({ id: offerId, type: 'offer' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, currentOffer]);

  const currentActiveStep = currentApproval?.approval_steps.find(
    (step) => step?.status === 'in_progress'
  );
  const currentStepUsers =
    currentActiveStep?.approval_user_groups?.[0]?.approval_users
      .filter((user) => user?.approval_log === null)
      ?.map((userObj) => userObj?.user?.id);
  // Only check if user is approver when currUserId is available (not bootstrapping)
  const isCurrentUserApprover =
    !isBootstraping && currUserId && currentStepUsers?.includes(currUserId);

  const toast = useShowToast();

  const [approverAcceptAction, { isLoading: isApproveActionLoading }] =
    useApproveRejectActionMutation();

  const [approverRejectAction, { isLoading: isRejectActionLoading }] =
    useApproveRejectActionMutation();

  const handleDrawerSubmit = () => {
    approverAcceptAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'approve',
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to approve the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer approved successfully`,
        });
        if (isMobile) {
          history.push('/tasks/jobs/approvals');
          modalActions?.onClose();
          return;
        }
        modalActions?.onClose();
        onSuccess();
      }
    });
  };

  const handleApproveOnBehalf = (userId) => {
    approverAcceptAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'approve',
        user_id: userId,
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to approve the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer approved successfully`,
        });
        approverActions?.onClose();
        statusMenuActions?.onClose();
        onSuccess();
        if (isMobile) {
          modalActions?.onClose();
        }
      }
    });
  };

  const handleReject = (values) => {
    approverRejectAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'reject',
        note: values?.note || '--',
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to reject the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer has been rejected`,
        });
        modalActions?.onClose();
        setShowRejectModal(false);
        onSuccess();
      }
    });
  };

  const moreCtaConfig = [
    {
      id: 'reject',
      Comp: () => {
        return (
          <Button
            variant="secondary"
            w={{ base: '100%', md: 'auto' }}
            onClick={() => {
              setShowRejectModal(true);
            }}
            loadingText="Updating"
            isLoading={isRejectActionLoading}
            disabled={isApproveActionLoading}
          >
            <Icon mr="5px" color="ats-negative" as={MinusCircle} />
            <KText size="sm-medium" color="primary-500">
              Reject
            </KText>
          </Button>
        );
      },
    },
  ];

  const renderStatus = (showBorder = true) => {
    return (
      <Flex
        gap="0.5em"
        alignItems="center"
        border={showBorder && '1px solid'}
        borderRadius="6px"
        w="fit-content"
        p="0.125em 0.5em"
        borderColor="ats-border-color"
      >
        <KText color="secondary-900" size="sm-semi-bold">
          Approval status
        </KText>
      </Flex>
    );
  };

  const RenderStatusAction = () => {
    const { isOpen, onOpen, onClose } = statusMenuActions;

    if (currentOffer?.offer_status === 'pending_approval')
      return (
        <Box mt={{ base: 2, md: '24px' }} mr={{ base: 0, md: '24px' }}>
          <Menu isOpen={isOpen} onClose={onClose}>
            <MenuButton
              as={Button}
              onClick={onOpen}
              w="fit-content"
              variant="ats-secondary"
              rightIcon={<ChevronDown />}
            >
              {renderStatus(false)}
            </MenuButton>
            <MenuList
              w={{ base: '100%', md: '400px' }}
              maxH={{ base: '60vh', md: 'unset' }}
              overflowY="auto"
              boxShadow="drop-shadows-bottom"
              zIndex={99}
            >
              <ApprovalFlowSteps
                config={currentApproval}
                onClose={onClose}
                isLoading={isCurrentApprovalLoading}
                offerId={offerId}
                entityId={offerId}
                entityType="offer"
                onSuccess={() => {
                  getApprovalData({ id: offerId, type: 'offer' });
                }}
                renderModalInParent={true}
                handleModalOpen={approverActions.onOpen}
                handleUserSelect={setSelectedUser}
              />
            </MenuList>
          </Menu>
        </Box>
      );
    else {
      return (
        <>
          {statusMap[currentOffer?.offer_status] && (
            <MenuButton
              as={Button}
              onClick={(event) => {
                event?.stopPropagation();
                onOpen();
              }}
              variant="ats-pending"
              borderColor={'transparent'}
              rightIcon={
                <ChevronDown color="#D98647" height="16px" width={'16px'} />
              }
              maxH="24px"
            >
              <KText color="#D98647" size="sm-semi-bold">
                {statusMap[currentOffer?.offer_status]}
              </KText>
            </MenuButton>
          )}
        </>
      );
    }
  };

  const statusMap = {
    draft: 'draft',
    approved_internal: 'offer approved',
    rejected_internal: 'offer rejected',
    candidate_email_in_progress: 'sending offer',
    candidate_email_failed: 'unable to send offer',
    awaiting_on_candidate: 'offer sent',
    accepted_by_candidate: 'accepted by candidate',
    rejected_by_candidate: 'rejected by candidate',
  };

  return (
    <>
      {modalActions?.isOpen && (
        <Drawer
          drawerActions={modalActions}
          hideCloseButton={isMobile}
          onDrawerSubmit={handleDrawerSubmit}
          drawerContentProps={{
            minW: { base: '100%', md: '750px' },
            p: { base: 0, md: 2 },
            borderTopLeftRadius: { base: 0, md: 'xl' },
          }}
          drawerBodyProps={{
            // Drawer body is the scroll container: override the default h="100%"
            // (which pushed the scrollable bottom off-screen) to fill remaining
            // space and scroll within it.
            overflow: 'auto',
            flex: '1 1 0%',
            minH: 0,
            h: 'auto',
          }}
          headerRightAdditionalComponent={
            !hideApprovalStatus && (
              <Flex alignItems="center" p="8px 16px">
                <RenderStatusAction />
              </Flex>
            )
          }
          disableCancel
          moreCtaConfig={moreCtaConfig}
          showHeader={!isMobile}
          headerText={
            <Flex alignItems="center" gap="8px" flexWrap="wrap">
              <KText variant="heading_2">Approve offer for</KText>
              {candidate_id && (
                <Flex
                  alignItems="center"
                  gap="6px"
                  cursor="pointer"
                  onClick={goToCandidateJob}
                >
                  <Avatar size={18} shape="square" name={candidate_name} />
                  <KText
                    size="md-medium"
                    color="primary-600"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {candidate_name}
                  </KText>
                  <Icon
                    as={LinkExternal02}
                    width="16px"
                    height="16px"
                    color="primary-600"
                  />
                </Flex>
              )}
            </Flex>
          }
          showFooter={!isMobile && isCurrentUserApprover && isPendingApproval}
          ctaProps={{
            loadingText: 'Updating',
            isLoading: isApproveActionLoading,
            disabled: isRejectActionLoading,
            w: { base: '100%', md: 'auto' },
          }}
          ctaLabel="Approve offer"
        >
          {isMobile && (
            <Box position="relative" flexShrink={0}>
              <Center
                w="48px"
                h="48px"
                left="12px"
                top="3px"
                pos="absolute"
                bg="#fff"
                borderRadius="full"
                cursor="pointer"
                onClick={modalActions?.onClose}
                zIndex={1}
              >
                <Icon as={X} w="20px" h="20px" />
              </Center>

              <Box pl="64px" pr="16px" py="12px">
                <Flex alignItems="center" gap="8px" flexWrap="wrap">
                  <KText size="lg-semi-bold" color="secondary-900">
                    Approve offer for
                  </KText>
                  {candidate_id && (
                    <Flex
                      alignItems="center"
                      gap="6px"
                      cursor="pointer"
                      onClick={goToCandidateJob}
                    >
                      <Avatar size={24} shape="square" name={candidate_name} />
                      <KText size="md-medium" color="primary-600">
                        {candidate_name}
                      </KText>
                      <Icon
                        as={LinkExternal02}
                        width="16px"
                        height="16px"
                        color="primary-600"
                      />
                    </Flex>
                  )}
                </Flex>
              </Box>
            </Box>
          )}

          <Flex
            flexDir="column"
            gap={{ base: '6px', md: '10px' }}
            p={{ base: '8px', md: '8px 16px' }}
          >
            {isOfferFetching || (isPendingApproval && isBootstraping) ? (
              // Full-page loader: minH centers the spinner (h="100%" didn't resolve
              // without a definite parent height and rendered small up top).
              <Flex
                minH="80vh"
                w="100%"
                alignItems="center"
                justifyContent="center"
              >
                <Spinner color="primary-800" size="xl" />
              </Flex>
            ) : (
              <>
                <CollapsibleSection title="Details" defaultIsOpen={true}>
                  <OfferDetailsSection currentOffer={currentOffer} sectioned />
                </CollapsibleSection>
                {offerFiles.length > 0 && (
                  <CollapsibleSection
                    title="Offer documents"
                    defaultIsOpen={true}
                  >
                    <Flex flexDir="column" gap="8px" p="12px">
                      {offerFiles.map((file, i) => (
                        <ViewFile
                          key={i}
                          file={{
                            url: file.url,
                            filename:
                              file.filename ||
                              `Offer document${offerFiles.length > 1 ? ` ${i + 1}` : ''}`,
                            content_type: file.content_type,
                          }}
                          showIcon={true}
                          showDelete={false}
                          showDownload={true}
                          showPreview={true}
                          hideTextReference={true}
                        />
                      ))}
                    </Flex>
                  </CollapsibleSection>
                )}
              </>
            )}
          </Flex>

          {/* --- MOBILE FOOTER BUTTONS --- */}
          {isMobile && isCurrentUserApprover && isPendingApproval && (
            <Flex
              position="sticky"
              bottom="0"
              w="100%"
              p="12px"
              bg="white"
              boxShadow="drop-shadows-bottom"
              gap="8px"
            >
              {/* Reject Button */}
              <Button
                flex="1"
                variant="mobile-danger-outline"
                onClick={() => setShowRejectModal(true)}
                isLoading={isRejectActionLoading}
                disabled={isApproveActionLoading}
              >
                Reject
              </Button>

              <Button
                flex="1"
                variant="mobile-primary-lg"
                onClick={handleDrawerSubmit}
                isLoading={isApproveActionLoading}
                disabled={isRejectActionLoading}
              >
                Approve Offer
              </Button>
            </Flex>
          )}
        </Drawer>
      )}
      {showRejectModal && (
        <ModalWithForm
          isOpen={true}
          onClose={() => {
            setShowRejectModal(false);
          }}
          headerText={'Reason for rejection?'}
          subHeaderText={
            'Provide detailed feedback on why you are rejecting this offer'
          }
          primaryCtaText="Reject"
          formConfig={rejectApproveFields}
          handleSubmit={handleReject}
          isCtaLoading={isRejectActionLoading}
          formContainerStyles={{
            m: { base: '0', md: '-10px 0' },
          }}
          skipDirtyCheck={true}
        />
      )}
      {approverActions?.isOpen && (
        <KModal
          isOpen={approverActions?.isOpen}
          onClose={approverActions?.onClose}
          title={`Approve on behalf of ${selectedUser?.user?.name || ''}`}
          ctaLabel="Approve"
          loadingText="Approving"
          ctaAction={(event) => {
            event?.stopPropagation();
            if (selectedUser?.user?.id) {
              handleApproveOnBehalf(selectedUser.user.id);
            }
          }}
          size={modalSize}
          closeOnOverlayClick={false}
        >
          <Flex flexDir="column" gap="10px">
            <KText>
              Approving for {selectedUser?.user?.name} will move the approval
              flow forward as though they did it.
            </KText>
            <KText mb="10px">This is permanent and cannot be reversed.</KText>
          </Flex>
        </KModal>
      )}
    </>
  );
};
